import * as React from 'react';
import { CardHeader, Grid, Skeleton, Tab, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import Row from 'src/frontend-common/components/Row';
import { useGetFeatureAnalysisResultQuery } from 'src/redux/services/imports';
import { CardResponsive } from 'src/style/common';
import { format } from 'date-fns';
import { TableRowWrapper } from 'src/pages/FeatureAnalysis/style';
import { tableCells } from 'src/pages/FeatureAnalysis/utils';
import AccuracyAnalysis from 'src/pages/FeatureAnalysis/AccuracyAnalysis';
import FeatureImportance from 'src/pages/FeatureAnalysis/FeatureImportance';
import { CustomTabs } from 'src/frontend-common/style/common';
import RuleRaccomendations from './RuleRaccomedations';
import Sparkles from 'src/frontend-common/components/Icons/Sparkles';
import { IAlertAnalytic } from 'src/frontend-common/dto/Interfaces/Analytics';
import EmptyState from 'src/frontend-common/components/EmptyState';

const FeatureAnalysis: React.FC = () => {
  const [verticalBarChartData, setVerticalBarChartData] = React.useState<IAlertAnalytic[]>();
  const [featureDistributionPerClass, setFeatureDistributionPerClass] = React.useState<{
    true?: { ko: number; ok: number };
    false?: { ko: number; ok: number };
  }>();

  const [koDataArray, setKoDataArray] = React.useState();
  const [productLabels, setProductLabels] = React.useState<string[]>();
  const [featureImportanceChart, setFeatureImportanceChart] = React.useState<number>(0);
  const [featureNames, setFeatureNames] = React.useState<string[]>();
  const [featureName, setFeatureName] = React.useState<string>();
  const [product, setActiveProduct] = React.useState<string>();
  const [activeTab, setActiveTab] = React.useState<number>(0);

  const [fileDetails, setFileDetails] = React.useState<{
    original_file_name: string;
    file_upload_time: string;
    n_records: number;
    n_errors: number;
    n_matching_ids: number;
    n_missing_ids: number;
    created_at: string;
    ok_label_details: string;
    ko_label_details: string;
  }>();

  const id = useParams<{ id: string }>();
  const { data: dataAnalysis, isError: isErrorDataAnalysis, isLoading: isLoadingDataAnalysis } = useGetFeatureAnalysisResultQuery({ id: id.id || '' });

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const tabs = ['Accuracy Analysis', 'Feature Importance', 'Recommendations'];
  const theme = useTheme();

  return (
    <DashboardPageLayout title="Insight">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CardResponsive>
            <CardHeader title="Upload details" />
            <Table>
              <TableHead>
                <TableRow>
                  {tableCells.map((tableCell) => (
                    <TableCell align="left" key={tableCell}>
                      <Row>
                        <>{tableCell}</>
                      </Row>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody style={{ backgroundColor: 'white' }}>
                <TableRowWrapper>
                  <TableCell align="left">{fileDetails?.original_file_name || <Skeleton width={53} />}</TableCell>
                  <TableCell align="left">
                    {fileDetails?.n_records || fileDetails?.n_records === 0 ? fileDetails?.n_records : <Skeleton width={53} />}
                  </TableCell>
                  <TableCell align="left">{fileDetails?.n_errors || fileDetails?.n_errors === 0 ? fileDetails?.n_errors : <Skeleton width={53} />}</TableCell>
                  <TableCell align="left">
                    {fileDetails?.n_matching_ids || fileDetails?.n_matching_ids === 0 ? fileDetails?.n_matching_ids : <Skeleton width={53} />}
                  </TableCell>
                  <TableCell align="left">
                    {fileDetails?.n_missing_ids || fileDetails?.n_missing_ids === 0 ? fileDetails?.n_missing_ids : <Skeleton width={53} />}
                  </TableCell>
                  <TableCell align="left">{fileDetails?.ok_label_details || <Skeleton width={53} />}</TableCell>
                  <TableCell align="left">{fileDetails?.ko_label_details || <Skeleton width={53} />}</TableCell>
                  <TableCell align="left" style={{ textAlign: 'left' }}>
                    {fileDetails?.created_at ? format(new Date(fileDetails?.created_at), 'dd LLLL yyyy, HH:mm') : <Skeleton width={53} />}
                  </TableCell>
                </TableRowWrapper>
              </TableBody>
            </Table>
          </CardResponsive>
        </Grid>
        <Grid item xs={12}>
          <CustomTabs
            value={featureImportanceChart}
            indicatorColor="primary"
            style={{ marginTop: 30, marginBottom: 15 }}
            onChange={(e: any, _newValue) => setFeatureImportanceChart(_newValue)}
          >
            {tabs.map((tab, index) => (
              <Tab
                label={
                  tab === 'Recommendations' ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {tab}{' '}
                      <div style={{ marginLeft: '3px' }}>
                        <Sparkles stroke={2 !== featureImportanceChart ? theme.palette.text.secondary : theme.palette.primary.main} />
                      </div>
                    </div>
                  ) : (
                    tab
                  )
                }
                {...a11yProps(index)}
                key={tab}
              />
            ))}
          </CustomTabs>
        </Grid>

        {featureImportanceChart === 0 && (
          <AccuracyAnalysis
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            a11yProps={a11yProps}
            dataAnalysis={dataAnalysis}
            isErrorDataAnalysis={isErrorDataAnalysis}
            isLoadingDataAnalysis={isLoadingDataAnalysis}
            setFileDetails={setFileDetails}
            setProductLabels={setProductLabels}
            setVerticalBarChartData={setVerticalBarChartData}
            setKoDataArray={setKoDataArray}
            setActiveProduct={setActiveProduct}
            setFeatureName={setFeatureName}
          />
        )}
        {featureImportanceChart === 1 && (
          <FeatureImportance
            dataAnalysis={dataAnalysis}
            verticalBarChartData={verticalBarChartData}
            product={product}
            productLabels={productLabels}
            setActiveProduct={setActiveProduct}
            featureDistributionPerClass={featureDistributionPerClass}
            setFeatureDistributionPerClass={setFeatureDistributionPerClass}
            koDataArray={koDataArray}
            setKoDataArray={setKoDataArray}
            featureNames={featureNames}
            featureName={featureName}
            setFeatureName={setFeatureName}
            setFeatureNames={setFeatureNames}
            a11yProps={a11yProps}
          />
        )}

        {featureImportanceChart === 2 && (dataAnalysis?.data?.item ? <RuleRaccomendations appKey={dataAnalysis.data.item.app_key} /> : <EmptyState />)}
      </Grid>
    </DashboardPageLayout>
  );
};

export default FeatureAnalysis;
